import { ChatBot } from '@/components/shared/ChatBot';
import { CreateCampaignInput, KPI, useCampaigns } from '@/hooks/useCampaigns';
import {
  CampaignSetupQuestions,
  useCampaignSetupAgent,
} from '@/hooks/useCampaignSetupAgent';
import { getCampaignsQueryKey } from '@/utils/query-keys';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { parse } from 'date-fns';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

interface CampaignSetupBotProps {
  onSuccess?: (campaignId: string) => void;
}

const validationSchema = yup.object().shape({
  name: yup.string().required('Campaign name is required'),
  start_date: yup
    .date()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return parse(originalValue.trim(), 'dd/MM/yyyy', new Date());
      }

      return value;
    })
    .nonNullable()
    .required('Start date is required')
    .min(
      new Date(new Date().setHours(0, 0, 0, 0)),
      'Campaign start date cannot be in the past',
    ),
  end_date: yup
    .date()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return parse(originalValue.trim(), 'dd/MM/yyyy', new Date());
      }

      return value;
    })
    .nonNullable()
    .required('End date is required')
    .min(yup.ref('start_date'), 'End date must be after start date'),
  key_dates: yup
    .array()
    .of(
      yup.object().shape({
        date: yup
          .date()
          .required('Key date is required')
          .test(
            'is-after-start',
            'Key date must be after start date',
            function (value) {
              const start = this.parent?.start_date;

              return !start || !value || value >= start;
            },
          )
          .test(
            'is-before-end',
            'Key date must be before end date',
            function (value) {
              const end = this.parent?.end_date;

              return !end || !value || value <= end;
            },
          ),
        context: yup
          .string()
          .required('Context is required for each key date')
          .min(3, 'Context must be at least 3 characters long')
          .matches(/^[^,]+$/, 'Context cannot contain commas'),
      }),
    )
    .default([]),
  objectives: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one objective is required'),
  kpi: yup.mixed<KPI>().oneOf(Object.values(KPI)).required('KPI is required'),
}) as yup.ObjectSchema<CreateCampaignInput>;

export const CampaignSetupBot = ({ onSuccess }: CampaignSetupBotProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const { createCampaign, recommendedKpi } = useCampaigns();

  const [isFinalising, setIsFinalising] = useState(false);

  const { setValue, watch } = useForm<CreateCampaignInput>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: undefined,
      key_dates: [],
      start_date: undefined,
      end_date: undefined,
      objectives: [],
      kpi: undefined,
    },
  });

  const formValues = watch();

  const handleSubmit = async (data: CampaignSetupQuestions): Promise<void> => {
    try {
      setIsFinalising(true);

      const campaign = await createCampaign.mutateAsync(data);

      // This allows us to wait for the active team to be fetched again before we show the toast
      await new Promise<void>((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: getCampaignsQueryKey(),
        });

        const unsubscribe = observer.subscribe((result) => {
          if (!result.isFetching) {
            unsubscribe();
            resolve();
          }
        });
      });

      toast.success('Campaign created successfully!');
      setIsFinalising(false);
      onSuccess?.(campaign.id);
    } catch (error) {
      setIsFinalising(false);
      console.error('Failed to create campaign:', error);
      toast.error('Failed to create campaign. Please try again.');
    }
  };

  const { visibleMessages, appendMessage, isLoading, suggestions } =
    useCampaignSetupAgent({
      setValue,
      formValues,
      inputRef,
      validationSchema,
      handleSubmit,
    });

  return (
    <ChatBot
      visibleMessages={visibleMessages}
      isLoading={isLoading || createCampaign.isPending}
      appendMessage={appendMessage}
      inputRef={inputRef}
      suggestions={suggestions}
      disabled={!formValues.kpi}
      overlayLoader={{
        message: recommendedKpi.isLoading
          ? 'Just analysing your data. This may take a little while, hold tight...'
          : 'Creating campaign...',
        isLoading: recommendedKpi.isLoading || isFinalising,
      }}
    />
  );
};
